import {
  Chat,
  Edit,
  OverflowMenuVertical,
  Renew,
  SendAlt,
  TrashCan,
} from '@carbon/icons-react';
import type { Theme } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type { SxProps } from '@mui/system';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ReviewComment } from './types';

// Validation constants (must match backend)
const COMMENT_MAX_LENGTH = 10000;

// Shared styles
const getTextFieldSx = (theme: Theme): SxProps<Theme> => ({
  '& .MuiInputBase-root': {
    fontSize: '0.875rem',
    backgroundColor: theme.palette.background.paper,
  },
});

const actionButtonsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1,
  mt: 1,
};

// CommentItem sub-component for rendering individual comments
interface CommentItemProps {
  comment: ReviewComment;
  index: number;
  isEditing: boolean;
  editContent: string;
  onEditContentChange: (content: string) => void;
  onEditKeyDown: (e: React.KeyboardEvent) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, commentId: string) => void;
  onRetry?: (id: string) => void;
  isDisabled: boolean;
}

const CommentItem = ({
  comment,
  index,
  isEditing,
  editContent,
  onEditContentChange,
  onEditKeyDown,
  onSaveEdit,
  onCancelEdit,
  onMenuOpen,
  onRetry,
  isDisabled,
}: CommentItemProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mb: index > 0 ? 0 : 0.25,
        py: 0.25,
        position: 'relative',
      }}
    >
      {/* Error state for failed comments */}
      {comment.error && (
        <Alert
          severity='error'
          sx={{ mb: 1, py: 0.5 }}
          action={
            onRetry && (
              <Button
                color='inherit'
                size='small'
                startIcon={<Renew size={16} />}
                onClick={() => onRetry(comment.id)}
              >
                Retry
              </Button>
            )
          }
        >
          Failed to save
        </Alert>
      )}

      {isEditing ? (
        <Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={12}
            value={editContent}
            onChange={(e) => onEditContentChange(e.target.value)}
            onKeyDown={onEditKeyDown}
            autoFocus
            size='small'
            inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
            sx={{
              mb: 1,
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
              },
            }}
          />
          <Box sx={actionButtonsSx}>
            <Button size='small' onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button
              size='small'
              variant='contained'
              onClick={onSaveEdit}
              disabled={!editContent.trim()}
            >
              Save
            </Button>
          </Box>
        </Box>
      ) : (
        <Paper
          elevation={index === 0 ? 1 : 0}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            p: 1.5,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            // Reply styling for comments after the first one
            ...(index > 0 && {
              ml: 0,
              borderLeft: `3px solid ${theme.palette.divider}`,
              borderRadius: 0,
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
            }),
          }}
        >
          <Box
            sx={{
              mr: 1.5,
              color: theme.palette.text.secondary,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              height: 'calc(0.875rem * 1.5)',
            }}
          >
            <Chat size={16} style={{ fill: theme.palette.text.primary }} />
          </Box>
          <Typography
            component='div'
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: theme.palette.text.primary,
              fontSize: '0.875rem',
              lineHeight: 1.5,
              flex: 1,
            }}
          >
            {comment.content}
          </Typography>
          {!isDisabled && (
            <Box
              sx={{
                ml: 1,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                height: 'calc(0.875rem * 1.5)',
              }}
            >
              <IconButton
                size='small'
                onClick={(e) => onMenuOpen(e, comment.id)}
                sx={{ p: 0.5 }}
              >
                <OverflowMenuVertical
                  size={16}
                  style={{ fill: theme.palette.text.primary }}
                />
              </IconButton>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

// Shared CommentInput component to reduce duplication
interface CommentInputProps {
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isDisabled: boolean;
  autoFocus?: boolean;
  hasTopMargin?: boolean;
}

const CommentInput = ({
  inputRef,
  value,
  onChange,
  onKeyDown,
  onSubmit,
  onCancel,
  isDisabled,
  autoFocus = false,
  hasTopMargin = false,
}: CommentInputProps) => {
  const theme = useTheme();
  const canSubmit = value.trim() && !isDisabled;

  return (
    <Box sx={{ mt: hasTopMargin ? 1 : 0 }}>
      <TextField
        inputRef={inputRef}
        autoFocus={autoFocus}
        fullWidth
        multiline
        minRows={2}
        maxRows={20}
        placeholder='Add your comment... (Cmd/Ctrl+Enter to submit, Esc to cancel)'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={isDisabled}
        variant='outlined'
        size='small'
        inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
        sx={getTextFieldSx(theme)}
      />
      <Box sx={actionButtonsSx}>
        <Button
          size='small'
          onClick={onCancel}
          disabled={isDisabled}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          size='small'
          variant='contained'
          onClick={onSubmit}
          disabled={!canSubmit}
          startIcon={
            <SendAlt
              size={16}
              style={!canSubmit ? { fill: theme.palette.text.primary } : {}}
            />
          }
          sx={{ textTransform: 'none' }}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

interface InlineCommentBlockProps {
  comments: ReviewComment[];
  showInput: boolean;
  /** When true, only shows the input (no comments). Used to avoid flash when comments are shown separately. */
  inputOnly?: boolean;
  onSubmit: (content: string) => void;
  onCancel: () => void;
  onEdit: (id: string, content: string) => void;
  onDelete: (id: string) => void;
  onRetry?: (id: string) => void;
  isDisabled?: boolean;
}

export const InlineCommentBlock = ({
  comments,
  showInput,
  inputOnly = false,
  onSubmit,
  onCancel,
  onEdit,
  onDelete,
  onRetry,
  isDisabled = false,
}: InlineCommentBlockProps) => {
  const theme = useTheme();
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    commentId: string;
  } | null>(null);
  const [isCompactInputExpanded, setIsCompactInputExpanded] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const compactInputRef = useRef<HTMLTextAreaElement>(null);

  const showCompactInput =
    !inputOnly && comments.length > 0 && !showInput && !isDisabled;

  // Auto-focus when input opens
  useEffect(() => {
    if (showInput) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showInput]);

  // Auto-focus compact input when expanded
  useEffect(() => {
    if (isCompactInputExpanded) {
      const timer = setTimeout(() => {
        compactInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCompactInputExpanded]);

  const handleSubmit = useCallback(() => {
    if (newComment.trim()) {
      onSubmit(newComment.trim());
      setNewComment('');
      setIsCompactInputExpanded(false);
    }
  }, [newComment, onSubmit]);

  const handleCancelCompactInput = useCallback(() => {
    setNewComment('');
    setIsCompactInputExpanded(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, isCompact = false) => {
      e.stopPropagation();
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (isCompact) {
          handleCancelCompactInput();
        } else {
          onCancel();
        }
      }
    },
    [handleSubmit, handleCancelCompactInput, onCancel],
  );

  const handleStartEdit = (comment: ReviewComment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
    setMenuAnchor(null);
  };

  const handleSaveEdit = () => {
    if (editingId && editContent.trim()) {
      onEdit(editingId, editContent.trim());
      setEditingId(null);
      setEditContent('');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    commentId: string,
  ) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, commentId });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDelete = (commentId: string) => {
    onDelete(commentId);
    setMenuAnchor(null);
  };

  // When inputOnly is true and there's no input to show, render nothing
  if (inputOnly && !showInput) {
    return null;
  }

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        px: 2,
        py: 1.5,
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      {/* Existing comments */}
      {!inputOnly &&
        comments.map((comment, index) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            index={index}
            isEditing={editingId === comment.id}
            editContent={editContent}
            onEditContentChange={setEditContent}
            onEditKeyDown={handleEditKeyDown}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onMenuOpen={handleMenuOpen}
            onRetry={onRetry}
            isDisabled={isDisabled}
          />
        ))}

      {/* Context menu for edit/delete */}
      {!inputOnly && (
        <Menu
          anchorEl={menuAnchor?.el}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              const comment = comments.find(
                (c) => c.id === menuAnchor?.commentId,
              );
              if (comment) handleStartEdit(comment);
            }}
          >
            <ListItemIcon>
              <Edit size={16} />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuAnchor?.commentId) handleDelete(menuAnchor.commentId);
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.error.main }}>
              <TrashCan size={16} />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.error.main }}>
              Delete
            </ListItemText>
          </MenuItem>
        </Menu>
      )}

      {/* Compact input placeholder - click to expand */}
      {showCompactInput && !isCompactInputExpanded && (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            placeholder='Add another comment...'
            onClick={() => setIsCompactInputExpanded(true)}
            onFocus={() => setIsCompactInputExpanded(true)}
            size='small'
            variant='outlined'
            InputProps={{
              readOnly: true,
              sx: { cursor: 'pointer' },
            }}
            sx={{
              ...getTextFieldSx(theme),
              '& .MuiInputBase-root': {
                ...((getTextFieldSx(theme) as Record<string, unknown>)[
                  '& .MuiInputBase-root'
                ] as Record<string, unknown>),
                cursor: 'pointer',
              },
              '& .MuiInputBase-input': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      )}

      {/* Expanded compact input for follow-up comments */}
      {showCompactInput && isCompactInputExpanded && (
        <CommentInput
          inputRef={compactInputRef}
          value={newComment}
          onChange={setNewComment}
          onKeyDown={(e) => handleKeyDown(e, true)}
          onSubmit={handleSubmit}
          onCancel={handleCancelCompactInput}
          isDisabled={isDisabled}
          hasTopMargin
        />
      )}

      {/* New comment input (triggered by + button) */}
      {showInput && (
        <CommentInput
          inputRef={inputRef}
          value={newComment}
          onChange={setNewComment}
          onKeyDown={(e) => handleKeyDown(e, false)}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          isDisabled={isDisabled}
          autoFocus
          hasTopMargin={comments.length > 0}
        />
      )}
    </Box>
  );
};
